import { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Input, InputNumber, Modal, Select, Switch, Tag, Tooltip, notification } from 'antd';
import { CopyOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { Trash } from 'lucide-react';
import dayjs from 'dayjs';

import CustomFilter from '../common/CustomFilter';
import CustomTable from './CustomTable';


const nextDraftKey = () => `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;


const stripMeta = (row = {}) => {
  const output = { ...row };
  delete output.__draftKey;
  delete output.__isNew;
  delete output.__isDirty;
  delete output.__original;
  return output;
};


const hasRowChanged = (row, originalRow) =>
  JSON.stringify(stripMeta(row)) !== JSON.stringify(originalRow || {});


const normalizeRows = (rows = []) =>
  rows.map((row) => {
    const originalRow = row.__original ? { ...row.__original } : stripMeta(row);
    return {
      ...stripMeta(row),
      __draftKey: row.__draftKey || nextDraftKey(),
      __isNew: Boolean(row.__isNew),
      __isDirty: Boolean(row.__isNew || row.__isDirty),
      __original: originalRow,
    };
  });


const renderReadonlyValue = (column, value, row, context = {}) => {
  if (column.renderReadonly) {
    return column.renderReadonly(value, row, context);
  }

  if (column.type === 'boolean') {
    return value ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>;
  }

  return <span>{value ?? ''}</span>;
};


const EditableGridTable = ({
  data,
  columns,
  rowKey = 'id',
  canEdit = false,
  onSaveRows,
  onDeleteRow,
  getNewRow,
  cloneRow,
  note,
  toolbarLabel,
  searchKeys = [],
  searchPlaceholder = 'Search table',
  showAddButton = true,
  showCloneButton = true,
  toolbarPosition = 'top',
  sticky = false,
  scroll = { x: true },
  pagination = false,
  summary,
  loading = false,
  rowClassName,
  onRow,
  tableLayout = 'auto',
  externalDraftRows = null,
  onExternalDraftRowsConsumed,
  onEditModeChange,
  showSearch = true,
}) => {
  const [editedRows, setEditedRows] = useState(normalizeRows(data));
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setEditedRows(normalizeRows(data));
  }, [data]);

  useEffect(() => {
    onEditModeChange?.(isEditMode);
  }, [isEditMode, onEditModeChange]);

  useEffect(() => {
    if (!externalDraftRows?.length) {
      return;
    }

    setEditedRows((current) => {
      const additions = normalizeRows(
        externalDraftRows.map((row) => ({
          ...row,
          __isNew: true,
          __isDirty: true,
          __original: null,
        }))
      ).filter((candidate) => {
        const candidateSignature = JSON.stringify(stripMeta(candidate));
        return !current.some((row) => JSON.stringify(stripMeta(row)) === candidateSignature);
      });

      if (!additions.length) {
        return current;
      }

      return [...current, ...additions];
    });

    setIsEditMode(true);
    onExternalDraftRowsConsumed?.();
  }, [externalDraftRows, onExternalDraftRowsConsumed]);

  const filteredRows = useMemo(() => {
    if (!searchText.trim() || !searchKeys.length) {
      return editedRows;
    }

    const query = searchText.trim().toLowerCase();
    return editedRows.filter((row) =>
      searchKeys.some((key) => String(row[key] ?? '').toLowerCase().includes(query))
    );
  }, [editedRows, searchKeys, searchText]);

  const handleCellChange = (draftKey, field, value) => {
    setEditedRows((current) =>
      current.map((row) => {
        if (row.__draftKey !== draftKey) {
          return row;
        }

        const nextRow = { ...row, [field]: value };
        nextRow.__isDirty = row.__isNew || hasRowChanged(nextRow, row.__original);
        return nextRow;
      })
    );
  };

  const handleAddRow = () => {
    const newRow = {
      ...(getNewRow ? getNewRow() : {}),
      __isNew: true,
      __isDirty: true,
      __original: null,
      __draftKey: nextDraftKey(),
    };
    setEditedRows((current) => [...current, newRow]);
    setIsEditMode(true);
  };

  const handleCloneLastRow = () => {
    const lastRow = editedRows[editedRows.length - 1];
    if (!lastRow) {
      return;
    }

    const cloned = {
      ...(cloneRow ? cloneRow(lastRow) : lastRow),
      __isNew: true,
      __isDirty: true,
      __original: null,
      __draftKey: nextDraftKey(),
    };
    setEditedRows((current) => [...current, cloned]);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setEditedRows(normalizeRows(data));
    setIsEditMode(false);
  };

  const validateRows = () => {
    for (const row of editedRows) {
      for (const column of columns) {
        if (!column.required) {
          continue;
        }

        const value = row[column.key];
        if (value === undefined || value === null || value === '') {
          notification.error({
            message: 'Validation Error',
            description: `${column.title} is required.`,
          });
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!onSaveRows) {
      return;
    }
    if (!validateRows()) {
      return;
    }

    try {
      const dirtyRows = editedRows
        .filter((row) => row.__isNew || row.__isDirty)
        .map((row) => ({
          ...stripMeta(row),
          __isNew: Boolean(row.__isNew),
          __original: row.__original ? { ...row.__original } : null,
        }));

      if (!dirtyRows.length) {
        setIsEditMode(false);
        notification.info({
          message: 'No Changes',
          description: 'There are no pending edits to save.',
        });
        return;
      }

      await onSaveRows(
        dirtyRows,
        editedRows.map((row) => stripMeta(row))
      );
      setIsEditMode(false);
      notification.success({
        message: 'Saved',
        description: toolbarLabel || 'Table changes saved successfully.',
      });
    } catch (error) {
      notification.error({
        message: 'Save Failed',
        description: error?.response?.data?.message || error?.message || 'Table changes could not be saved.',
      });
    }
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: 'Delete this row?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (row.__isNew || !onDeleteRow) {
          setEditedRows((current) => current.filter((item) => item.__draftKey !== row.__draftKey));
          setIsEditMode(true);
          return;
        }

        try {
          await onDeleteRow(row);
          setEditedRows((current) => current.filter((item) => item.__draftKey !== row.__draftKey));
          setIsEditMode(true);
          notification.success({
            message: 'Deleted',
            description: 'Row deleted successfully.',
          });
        } catch (error) {
          notification.error({
            message: 'Delete Failed',
            description: error?.response?.data?.message || 'Row could not be deleted.',
          });
        }
      },
    });
  };

  const renderEditor = (column, row) => {
    const value = row[column.key];
    const commonProps = {
      placeholder: column.placeholder,
      disabled: column.disabled,
      style: { width: '100%' },
    };

    if (column.type === 'number') {
      return (
        <InputNumber
          {...commonProps}
          value={value}
          min={column.min}
          max={column.max}
          step={column.step || 1}
          onChange={(nextValue) => handleCellChange(row.__draftKey, column.key, nextValue)}
        />
      );
    }

    if (column.type === 'date') {
      return (
        <DatePicker
          {...commonProps}
          format="DD/MM/YYYY"
          placeholder="DD/MM/YYYY"
          inputReadOnly
          value={value ? dayjs(value) : null}
          onChange={(nextValue) =>
            handleCellChange(row.__draftKey, column.key, nextValue ? nextValue.format('YYYY-MM-DD') : '')
          }
        />
      );
    }

    if (column.type === 'select') {
      return (
        <Select
          {...commonProps}
          value={value}
          options={column.options || []}
          mode={column.mode}
          onChange={(nextValue) => handleCellChange(row.__draftKey, column.key, nextValue)}
        />
      );
    }

    if (column.type === 'boolean') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Switch
            checked={Boolean(value)}
            onChange={(checked) => handleCellChange(row.__draftKey, column.key, checked)}
          />
        </div>
      );
    }

    if (column.type === 'textarea') {
      return (
        <Input.TextArea
          {...commonProps}
          value={value}
          rows={2}
          onChange={(event) => handleCellChange(row.__draftKey, column.key, event.target.value)}
        />
      );
    }

    return (
      <Input
        {...commonProps}
        value={value}
        onChange={(event) => handleCellChange(row.__draftKey, column.key, event.target.value)}
      />
    );
  };

  const tableColumns = [
    ...columns.map((column) => ({
      title: <CustomFilter title={String(column.title || column.key).toUpperCase()} />,
      dataIndex: column.key,
      key: column.key,
      width: column.width,
      fixed: column.fixed,
      align: column.align || 'center',
      render: (_, row) => {
        if (column.render) {
          return column.render(row[column.key], row, { isEditMode, onChange: handleCellChange });
        }

        if (!isEditMode || column.editable === false || !canEdit) {
          return renderReadonlyValue(column, row[column.key], row, { isEditMode });
        }

        return renderEditor(column, row);
      },
    })),
  ];

  if (canEdit && isEditMode) {
    tableColumns.push({
      title: <CustomFilter title="DELETE" />,
      key: '__actions',
      align: 'center',
      fixed: 'right',
      width: 96,
      render: (_, row) => (
        <Tooltip title="Delete row">
          <Button danger onClick={() => handleDelete(row)}>
            <Trash size={16} />
          </Button>
        </Tooltip>
      ),
    });
  }

  const toolbar = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        padding: '16px 20px',
        borderTop: toolbarPosition === 'bottom' ? '1px solid #f0f0f0' : 'none',
        borderBottom: toolbarPosition === 'top' ? '1px solid #f0f0f0' : 'none',
        background: '#fafafa',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {showSearch ? (
          <Input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder={searchPlaceholder}
            style={{ width: 240 }}
          />
        ) : null}
        {note ? <span style={{ color: '#64748b', fontSize: 12 }}>{note}</span> : null}
      </div>

      {canEdit ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {showAddButton ? (
            <Button icon={<PlusOutlined />} type="primary" onClick={handleAddRow}>
              Add Row
            </Button>
          ) : null}
          {showCloneButton ? (
            <Button icon={<CopyOutlined />} onClick={handleCloneLastRow}>
              Clone Last Row
            </Button>
          ) : null}
          <Button
            icon={isEditMode ? <ReloadOutlined /> : <EditOutlined />}
            type={isEditMode ? 'default' : 'primary'}
            onClick={isEditMode ? handleCancel : () => setIsEditMode(true)}
          >
            {isEditMode ? 'Cancel' : 'Edit Mode'}
          </Button>
          {isEditMode ? (
            <Button
              icon={<SaveOutlined />}
              type="primary"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              onClick={handleSave}
            >
              Save Changes
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        borderRadius: 15,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      {toolbarPosition === 'top' ? toolbar : null}
      <CustomTable
        data={filteredRows}
        columns={tableColumns}
        bordered={false}
        loading={loading}
        pagination={pagination}
        rowKey={(record) => record.__draftKey || record[rowKey]}
        scroll={scroll}
        sticky={sticky}
        summary={summary}
        rowHoverable={!isEditMode}
        rowClassName={rowClassName}
        onRow={onRow}
        tableLayout={tableLayout}
      />
      {toolbarPosition === 'bottom' ? toolbar : null}
    </div>
  );
};


export default EditableGridTable;
